import { DateRange } from "../../interfaces/DatesGlobalInterface.type";

/** Action the chatbot performs for a matched intent. */
export enum ChatAction {
  NAVIGATE = "NAVIGATE",
  ANSWER = "ANSWER",
  /** Find a client, entity, or agrupation and update the active context. */
  SET_CONTEXT = "SET_CONTEXT",
  /** Detected a date period — broadcast it via SessionDataService.chatbotDateRange. */
  SET_DATE = "SET_DATE"
}

/** Kind of context entity a SET_CONTEXT intent searches for. */
export type ContextKind = "agrupation" | "entity" | "client";

/** Agrupation / entity / client name extracted from the message. */
export interface ContextTerm {
  term: string;
  kind: ContextKind;
  /**
   * True when inferred by the proper-noun heuristic rather than an explicit keyword
   * ("agrupación X", "entidad X", "cliente X"). Lets model search treat it as a manufacturer name.
   */
  implicit: boolean;
}

/** Signals extracted from one user message before any action is taken. */
export interface SemanticAnalysis {
  /** Normalised input text. */
  normalized: string;
  dateRange: DateRange | null;
  queryFilter: ChatbotQueryFilter | null;
  context: ContextTerm | null;
}

/** An intent paired with its specificity score (higher = more specific / preferred). */
export interface ScoredIntent {
  intent: Intent;
  /** 0–100 specificity score: longer/more-specific pattern matches score higher. */
  score: number;
}

/** Button rendered under a bot message; opens the help guide (optionally at a section). */
export interface ChatMessageAction {
  kind: "open-help";
  label: string;
  topicId: string;
  sectionId?: string;
}

/** One message in the chatbot conversation. */
export interface ChatMessage {
  id: string;
  content: string;
  role: "user" | "bot";
  timestamp: Date;
  actions?: ChatMessageAction[];
}

/** Recognised user intent: its match patterns, action and response. */
export interface Intent {
  id: string;
  patterns: RegExp[];
  action: ChatAction;
  /** Route path segment(s) joined by "/" — used when action === NAVIGATE. */
  payload?: string;
  /** Optional router navigation state attached to this route. */
  payloadHistoryData?: object;
  /**
   * Context kind to search when action === SET_CONTEXT.
   * 'agrupation' searches agrupations; 'entity' searches entities; 'client' searches clients.
   */
  contextKind?: ContextKind;
  response: string;
  /**
   * Short human-readable description of what this intent does, shown in the
   * Instructions panel. Populate for non-trivial intents; omit for sub-navigation
   * entries whose payload is already self-documenting.
   */
  description?: string;
  /**
   * When present, after navigating to this intent's payload the chatbot service
   * will compute a concrete ChatbotTableFilter from this spec and broadcast it via
   * SessionDataService.chatbotTableFilter so the target screen can pre-apply a
   * column filter to its TableControllerComponent.
   */
  tableFilter?: ChatbotTableFilterSpec;
}

/**
 * Static specification for a table column filter, embedded in an Intent definition.
 * The chatbot service resolves this to a ChatbotTableFilter at dispatch time.
 */
export interface ChatbotTableFilterSpec {
  /** Column title as defined in TableDataColumn.title (e.g. 'date-last-value'). */
  columnTitle: string;
  /** Comparison operator to apply. */
  operator: "<" | ">" | "<=" | ">=" | "==";
  /**
   * Days relative to today (positive = N days in the past).
   * E.g. 4 → cutoff = today − 4 days.
   */
  relativeDays: number;
  /**
   * Optional i18n key resolved at dispatch time via TranslateService.
   * When present, operator and relativeDays are ignored.
   * The translated value is used as a plain text search (espComparison: false),
   * allowing column filters on non-date string columns such as valve-state.
   */
  i18nValue?: string;
}

/**
 * Computed table column filter broadcast by the chatbot service.
 * The consuming screen reads SessionDataService.chatbotTableFilter on init and
 * passes it to its TableControllerComponent.initialSearch, then calls
 * clearChatbotTableFilter().
 */
export interface ChatbotTableFilter {
  /** Column title as defined in TableDataColumn.title. */
  columnTitle: string;
  /** Filter data in TableFilterService special-filter format, e.g. '<1712000000000', or a plain text value. */
  filterData: string;
  /** Human-readable label for the active-filter badge, e.g. '< 2026-04-16'. */
  filterDataString: string;
  /** True for date/numeric special-comparison filters; false for plain text search filters. */
  espComparison: boolean;
  /** Include null or empty column values when applying the filter. */
  showNull: boolean;
  /**
   * When set, the consuming component MUST re-translate this key at apply-time via
   * TranslateService.instant() to ensure the value matches the table cell rendering
   * context (where backend translations are already merged). Takes precedence over filterData.
   */
  i18nKey?: string;
}

/**
 * Pre-filled filter profile broadcast by the chatbot to a query/analysis screen.
 * The consuming screen reads `SessionDataService.chatbotQueryFilter` on init and
 * applies the corresponding preset, then calls `clearChatbotQueryFilter()`.
 */
export interface ChatbotQueryFilter {
  /**
   * Semantic profile controlling which filters to preset on the target screen.
   * Maps directly to a QUERIES entry by queryId.
   * - 'consumo-hora':       query 1 — meters by hourly consumption (requires consumo + dates).
   * - 'parados':            query 2 — stopped/no-communication meters (requires dates).
   * - 'consumo-negativo':   query 3 — meters with negative consumption (requires dates).
   * - 'fugas':              query 4 — hydraulic leak detection, consumo always > X (requires consumo + dates).
   * - 'picos-anomalos':     query 5 — abnormal consumption peaks (requires factorConsumoMedio % + dates).
   * - 'comparacion':        query 9 — consumption comparison between two date ranges (requires 4 dates).
   * - 'valores-iniciales':  query 10 — meters installed in range with initial values (requires dates).
   */
  profile:
    | "consumo-hora"
    | "parados"
    | "consumo-negativo"
    | "fugas"
    | "picos-anomalos"
    | "comparacion"
    | "valores-iniciales";
  /** ID of the QUERIES array entry to auto-select in QueriesComponent. */
  queryId: number;
  /**
   * Map of QueryDataInput.key → pre-filled value for the query’s numeric/text inputs.
   * e.g. `{ consumo: 0 }` tells the leaks query to search for consumption > 0.
   */
  inputValues?: Record<string, number | string | null>;
  /**
   * When true the consuming screen may auto-execute a READ-ONLY fetch once all
   * required inputs and dates are pre-filled. Must NEVER be set for actions that
   * write, modify or delete data — those require explicit user confirmation.
   * The component additionally verifies the action against CHATBOT_ALLOWED_AUTO_EXECUTE.
   */
  autoExecute?: boolean;
}

/** Category used to group sections in the concept map. */
export type MapGroup =
  | "dashboard"
  | "telecontrol"
  | "alarms"
  | "devices"
  | "devices-ops"
  | "gateways"
  | "orders"
  | "data-analysis"
  | "data-mgmt"
  | "files"
  | "sensory"
  | "network"
  | "maint-global"
  | "maint-entity"
  | "admin"
  | "users"
  | "install"
  | "subscriber";

/** Application section shown in the concept map, with optional navigation metadata. */
export interface MapSection {
  id: string;
  /** Font Awesome icon class (without the `fa` prefix — e.g. `fa-home`). */
  icon: string;
  labelKey: string;
  descKey: string;
  /** Route payload: path segments joined by "/", same format as Intent.payload. */
  payload: string;
  group: MapGroup;
  /** Profile strings that are allowed to access this section (mirrors the route expectedRole). */
  profiles: ReadonlyArray<string>;
  /** If set, this section is a sub-section of the named parent section id. */
  parentId?: string;
  /**
   * When false, this section appears in the tree as a label but has no navigation link.
   * Use for parent shell routes that have no component of their own.
   */
  navigable?: boolean;
  /**
   * When present, this section is only shown if the current client's clientId
   * is in this array. Mirrors the `client` restriction in navbar-menu.ts.
   */
  clientIds?: ReadonlyArray<number>;
  /**
   * When true, this section is only shown if the current client has the gas flag.
   * Mirrors the `visible: "gas"` restriction in navbar-menu.ts.
   */
  requiresGas?: true;
}
